import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Check,
  Film,
  MapPin,
  Tag,
  ShieldCheck,
  ArrowRightLeft,
  ClipboardList,
  X,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Settings,
} from 'lucide-react';
import {
  VSLProtection,
  DEFAULT_PROTECTION,
  COUNTRIES,
  REGIONS_BY_COUNTRY,
  LANGUAGES,
  UtmRule,
  UtmParam,
  UtmOperator,
  UtmAction,
  DeviceType,
} from './types';
import { VideoFolderPicker, VideoFolderPickerItem } from '@/components/shared/VideoFolderPicker';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { MultiSelectSearch } from './MultiSelectSearch';

type StepId = 'vsl' | 'location' | 'traffic' | 'devices' | 'proxy' | 'summary';

const STEPS: { id: StepId; label: string; icon: any }[] = [
  { id: 'vsl', get label() { return translateInterfaceText("VSL"); }, icon: Film },
  { id: 'location', get label() { return translateInterfaceText("Localização"); }, icon: MapPin },
  { id: 'traffic', get label() { return translateInterfaceText("Tráfego"); }, icon: Tag },
  { id: 'devices', get label() { return translateInterfaceText("Dispositivos"); }, icon: Monitor },
  { id: 'proxy', get label() { return translateInterfaceText("Proxy/VPN"); }, icon: ShieldCheck },
];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSave: (p: VSLProtection) => void;
  initial?: VSLProtection | null;
  videos?: VideoFolderPickerItem[];
}

export function CreateProtectionModal({ open, onOpenChange, onSave, initial, videos = [] }: Props) {
  useLanguage();
  const [step, setStep] = useState<StepId>('vsl');
  const [data, setData] = useState<VSLProtection>(() => buildInitial(initial));

  useEffect(() => {
    if (open) {
      setData(buildInitial(initial));
      setStep('vsl');
    }
  }, [open, initial]);

  const update = (patch: Partial<VSLProtection>) =>
    setData((d) => ({ ...d, ...patch, updatedAt: new Date().toISOString() }));

  const canSave = !!data.name && !!data.videoId && !!data.redirectVideoId;

  const handleSave = () => {
    if (!data.name) {
      toast({ title: translateInterfaceText("Sem nome da proteção"), description: translateInterfaceText("Informe um nome para a proteção."), variant: 'destructive' });
      setStep('vsl');
      return;
    }
    if (!data.videoId) {
      toast({ title: translateInterfaceText("Sem VSL protegida"), description: translateInterfaceText("Selecione a VSL que será protegida."), variant: 'destructive' });
      setStep('vsl');
      return;
    }
    if (!data.redirectVideoId) {
      toast({ title: translateInterfaceText("Sem vídeo alternativo"), description: translateInterfaceText("Selecione o vídeo alternativo para acessos bloqueados."), variant: 'destructive' });
      setStep('vsl');
      return;
    }
    onSave({ ...data, updatedAt: new Date().toISOString(), blockedAction: 'video' });
    onOpenChange(false);
  };

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const goNext = () => {
    if (step === 'vsl') {
      if (!data.name) {
        toast({ title: translateInterfaceText("Sem nome da proteção"), description: translateInterfaceText("Informe um nome para a proteção."), variant: 'destructive' });
        return;
      }
      if (!data.videoId) {
        toast({ title: translateInterfaceText("Sem VSL protegida"), description: translateInterfaceText("Selecione a VSL que será protegida."), variant: 'destructive' });
        return;
      }
      if (!data.redirectVideoId) {
        toast({ title: translateInterfaceText("Sem vídeo alternativo"), description: translateInterfaceText("Selecione o vídeo alternativo para acessos bloqueados."), variant: 'destructive' });
        return;
      }
    }
    if (stepIndex < STEPS.length - 1) setStep(STEPS[stepIndex + 1].id);
  };
  const goBack = () => {
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1].id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Dimensões por style inline para vencer o shadcn base (sm:w-auto +
        // sm:max-w-[560px]), que senão faz a largura seguir o conteúdo e o modal
        // "encolher" em steps com pouco conteúdo (ex.: Dispositivos).
        // Largura fixa de 980px, responsiva (cai p/ 95vw em telas estreitas).
        style={{
          width: 'min(980px, 95vw)',
          maxWidth: '95vw',
          height: 'min(820px, calc(100vh - 80px))',
          maxHeight: '90vh',
        }}
        className="p-0 gap-0 bg-white rounded-2xl overflow-hidden flex flex-col border-none shadow-2xl"
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-[#EEF2F7] flex items-center justify-between flex-shrink-0 bg-white">
          <div>
            <h2 className="text-[20px] font-bold text-[#0F172A] tracking-[-0.01em]">
              {initial ? translateInterfaceText("Editar proteção") : translateInterfaceText("Nova proteção")}
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1 font-normal leading-[1.4]">
              {translateInterfaceText("Configure tudo em uma única jornada")}</p>
          </div>
        </div>

        {/* Body: 2 columns */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* Sidebar steps */}
          <aside className="w-[72px] sm:w-[280px] border-r border-[#EEF2F7] bg-white py-8 flex-shrink-0 overflow-y-auto">
            <nav className="px-2 sm:px-4 space-y-2">
              {STEPS.map((s, idx) => {
                const Icon = s.icon;
                const active = s.id === step;
                const done = idx < stepIndex;
                return (
                  <button
                    key={s.id}
                    onClick={() => setStep(s.id)}
                    className={cn(
                      // Tamanho fixo idêntico em todos os tabs: h-56 + border-2
                      // reservada (transparente quando inativo) para o estado
                      // ativo nunca alterar a dimensão do botão. No mobile a
                      // sidebar vira só ícones (centralizados, sem label).
                      'w-full box-border flex items-center justify-center sm:justify-start gap-0 sm:gap-4 px-0 sm:px-5 h-[56px] rounded-2xl border-2 text-[14px] font-semibold transition-all duration-200 group relative',
                      active
                        ? 'border-[#2563FF] bg-[#EEF4FF] text-[#2563FF] font-bold'
                        : 'border-transparent text-[#64748B] hover:bg-[#FAFBFC] hover:text-[#111827]'
                    )}
                  >
                    <span
                      className={cn(
                        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 shadow-none border-none',
                        active
                          ? 'bg-[#2563FF] text-white'
                          : done
                          ? 'bg-[#DDFBEA] text-[#10B981]'
                          : 'bg-white border border-[#E5E7EB] text-[#9CA3AF] group-hover:border-[#D1D5DB]'
                      )}
                    >
                      {done ? <Check className="w-5 h-5 stroke-[3]" /> : <Icon className="w-5 h-5" />}
                    </span>
                    <span className="hidden sm:block truncate">{s.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main content area */}
          <div className="flex-1 min-w-0 flex flex-col bg-[#FAFBFC]">
            <ScrollArea className="flex-1">
              <div className="max-w-[760px] mx-auto w-full">
                <div className="px-4 sm:px-12 pt-6 sm:pt-8 pb-8 sm:pb-12">
                  {step === 'vsl' && <StepVSL data={data} update={update} videos={videos} />}
                  {step === 'location' && <StepLocation data={data} update={update} />}
                  {step === 'traffic' && <StepTraffic data={data} update={update} />}
                  {step === 'devices' && <StepDevices data={data} update={update} />}
                  {step === 'proxy' && <StepProxy data={data} update={update} />}

                  {/* Visual Flow Footer only on first step */}
                  {step === 'vsl' && (
                    <div className="mt-6">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-1.5 h-6 bg-[#2563EB] rounded-full" />
                      <h4 className="text-[16px] font-bold text-[#0F172A]">{translateInterfaceText("Visualização do fluxo")}</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_48px_1fr] items-center gap-4 p-4 sm:p-6 bg-white border border-[#EEF2F7] rounded-3xl shadow-sm">
                      {/* VSL Original */}
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-[#E0E7FF] text-[#2563EB] flex items-center justify-center text-[10px] font-bold">1</span>
                          <span className="text-[12px] font-bold text-[#334155] uppercase tracking-wider">{translateInterfaceText("VSL PROTEGIDA")}</span>
                        </div>
                        <div className="aspect-video bg-[#F9FAFB] rounded-2xl overflow-hidden border border-[#EEF2F7] relative shadow-inner">
                          {data.videoThumb ? (
                            <img src={data.videoThumb} alt={translateInterfaceText("VSL Original")} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#9CA3AF]">
                              <Film className="w-6 h-6 opacity-20" />
                              <span className="text-[11px] font-medium">{translateInterfaceText("Não selecionada")}</span>
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                            <p className="text-[11px] text-white font-semibold truncate">{data.videoName || translateInterfaceText("Aguardando seleção...")}</p>
                          </div>
                        </div>
                      </div>

                      {/* Transition */}
                      <div className="flex flex-col items-center justify-center gap-2 pt-0 sm:pt-8">
                        <div className="w-10 h-10 rounded-full bg-[#F0F5FF] border border-[#D1E0FF] flex items-center justify-center shadow-sm">
                          <ArrowRightLeft className="w-5 h-5 text-[#2563EB]" />
                        </div>
                        <span className="text-[10px] font-extrabold text-[#94A3B8] text-center uppercase tracking-[0.055em] leading-none px-1">{translateInterfaceText("Se")}<br/>{translateInterfaceText("Bloqueado")}</span>
                      </div>

                      {/* Alternative Video */}
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-[#FEE2E2] text-[#EF4444] flex items-center justify-center text-[10px] font-bold">2</span>
                          <span className="text-[12px] font-bold text-[#334155] uppercase tracking-wider">{translateInterfaceText("VÍDEO ALTERNATIVO")}</span>
                        </div>
                        <div className="aspect-video bg-[#F9FAFB] rounded-2xl overflow-hidden border border-[#EEF2F7] relative shadow-inner">
                          {data.redirectVideoThumb ? (
                            <img src={data.redirectVideoThumb} alt={translateInterfaceText("Vídeo Alternativo")} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#9CA3AF]">
                              <ArrowRightLeft className="w-6 h-6 opacity-20" />
                              <span className="text-[11px] font-medium">{translateInterfaceText("Aguardando seleção")}</span>
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                            <p className="text-[11px] text-white font-semibold truncate">{data.redirectVideoName || translateInterfaceText("Vídeo de segurança")}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </ScrollArea>
          </div>

          {/* Right summary section removed or integrated elsewhere if needed, 
              but requested image 2 shows a simpler 2-column layout (Steps | Content) */}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-[#EEF2F7] flex items-center justify-between flex-shrink-0 bg-white">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#334155] hover:text-[#0F172A] font-semibold text-[14px] h-12 px-6 hover:bg-[#F9FAFB] border border-[#E2E8F0] rounded-xl"
          >
            {translateInterfaceText("Cancelar")}</Button>
          <div className="flex items-center gap-4">
            {stepIndex > 0 && (
              <Button 
                variant="outline" 
                onClick={goBack} 
                className="h-12 px-8 rounded-xl border-[#E2E8F0] font-semibold text-[14px] text-[#334155] hover:bg-[#F9FAFB] hover:text-[#334155]"
              >
                {translateInterfaceText("Voltar")}</Button>
            )}
            {stepIndex < STEPS.length - 1 ? (
              <Button
                onClick={goNext}
                className="bg-[#2563FF] hover:bg-[#1D4ED8] text-white h-12 px-10 rounded-xl font-bold text-[14px] shadow-[0_4px_12px_rgba(37,99,235,0.2)] transition-all"
              >
                {translateInterfaceText("Continuar")}</Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className="bg-[#2563FF] hover:bg-[#1D4ED8] text-white h-12 px-10 rounded-xl font-bold text-[14px] shadow-[0_4px_12px_rgba(37,99,235,0.2)] transition-all"
              >
                {translateInterfaceText("Salvar proteção")}</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function buildInitial(initial?: VSLProtection | null): VSLProtection {
  if (initial) return { ...initial };
  return {
    ...DEFAULT_PROTECTION,
    id: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/* ---------- STEP COMPONENTS ---------- */

interface StepProps {
  data: VSLProtection;
  update: (p: Partial<VSLProtection>) => void;
}

function SectionTitle({ title }: { title: string }) {
  useLanguage();
  return (
    <div className="mb-8">
      <h3 className="text-[21px] font-bold text-[#0F172A] leading-[1.25] tracking-[-0.015em]">
        {title}
      </h3>
    </div>
  );
}

function StepVSL({ data, update, videos }: StepProps & { videos: VideoFolderPickerItem[] }) {
  useLanguage();
  return (
    <>
      <SectionTitle title={translateInterfaceText("Configuração dos vídeos")} />
      <div className="space-y-8">
        <div className="space-y-2">
          <Label className="text-[13px] font-semibold text-[#334155] leading-[1.3]">{translateInterfaceText("Nome da proteção")}</Label>
          <Input
            value={data.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder={translateInterfaceText("Ex: Proteção principal")}
            className="h-12 text-[14px] rounded-xl border-[#E5E7EB] bg-white focus-visible:ring-2 focus-visible:ring-[#2563EB]/20 focus-visible:border-[#2563EB]"
          />
        </div>

        {/* Os dois seletores se excluem: a VSL protegida e a alternativa precisam
            ser vídeos diferentes, então cada um oculta o vídeo escolhido no outro. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Label className="text-[13px] font-semibold text-[#334155] leading-[1.3]">{translateInterfaceText("VSL protegida")}</Label>
            <VideoFolderPicker
              value={data.videoId ?? undefined}
              videos={videos.filter((v) => v.id !== data.redirectVideoId)}
              onChange={(id) => {
                const v = videos.find((x) => x.id === id);
                update({
                  videoId: id,
                  videoName: v?.title,
                  videoThumb: v?.thumbnailUrl,
                  name: data.name || v?.title || '',
                });
              }}
              placeholder={translateInterfaceText("Selecionar VSL")}
              triggerClassName="flex h-12 w-full items-center justify-between gap-2 rounded-xl border border-[#E5E7EB] bg-white px-4 text-sm text-[#111827] hover:border-[#2563EB] transition-all"
            />

            {/* Removed redundant selection preview as requested */}
          </div>

          <div className="space-y-4">
            <Label className="text-[13px] font-semibold text-[#334155] leading-[1.3]">{translateInterfaceText("VSL alternativa")}</Label>
            <VideoFolderPicker
              value={data.redirectVideoId ?? undefined}
              videos={videos.filter((v) => v.id !== data.videoId)}
              onChange={(id) => {
                const v = videos.find((x) => x.id === id);
                update({
                  redirectVideoId: id,
                  redirectVideoName: v?.title,
                  redirectVideoThumb: v?.thumbnailUrl,
                  blockedAction: 'video'
                });
              }}
              placeholder={translateInterfaceText("Selecionar vídeo")}
              triggerClassName="flex h-12 w-full items-center justify-between gap-2 rounded-xl border border-[#E5E7EB] bg-white px-4 text-sm text-[#111827] hover:border-[#2563EB] transition-all"
            />

            {/* Removed redundant selection preview as requested */}
          </div>
        </div>
      </div>
    </>
  );
}

function StepLocation({ data, update }: StepProps) {
  useLanguage();
  const [activeCountryForRegions, setActiveCountryForRegions] = useState<string>(() => {
    return data.countries.length > 0 ? data.countries[0] : '';
  });

  useEffect(() => {
    if (data.countries.length === 0) {
      setActiveCountryForRegions('');
    } else if (!data.countries.includes(activeCountryForRegions)) {
      setActiveCountryForRegions(data.countries[0]);
    }
  }, [data.countries, activeCountryForRegions]);

  const activeCountryName = COUNTRIES.find((c) => c.code === activeCountryForRegions)?.name || '';
  const activeCountryRegions = activeCountryForRegions ? REGIONS_BY_COUNTRY[activeCountryForRegions] || [] : [];

  const handleCountryChange = (countries: string[]) => {
    const removedCountries = data.countries.filter((c) => !countries.includes(c));
    let updatedRegions = [...data.regions];
    
    removedCountries.forEach(countryCode => {
      const countryRegions = REGIONS_BY_COUNTRY[countryCode] || [];
      const regionCodes = countryRegions.map(r => r.code);
      updatedRegions = updatedRegions.filter(r => !regionCodes.includes(r));
    });

    update({ countries, regions: updatedRegions });
  };

  return (
    <>
      <SectionTitle title={translateInterfaceText("Controle por localização")} />

      <div className="space-y-3 mb-8">
        <Label className="text-[13px] font-semibold text-[#334155] leading-[1.3]">{translateInterfaceText("Modo de filtragem")}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(
            [
              {
                v: 'allow' as const,
                title: translateInterfaceText("Permitir selecionados"),
                desc: translateInterfaceText("Disponível só nestes locais."),
              },
              {
                v: 'block' as const,
                title: translateInterfaceText("Bloquear selecionados"),
                desc: translateInterfaceText("Esses locais serão bloqueados."),
              },
            ]
          ).map((opt) => {
            const active = data.locationMode === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => update({ locationMode: opt.v })}
                className={cn(
                  'text-left p-4 rounded-2xl border transition-all duration-200 shadow-sm',
                  active
                    ? 'border-[#2563EB] bg-[#F0F5FF]'
                    : 'border-[#E5E7EB] bg-white hover:border-[#D1E0FF] hover:bg-[#F9FAFB]'
                )}
              >
                <p className={cn("text-[14px] font-bold transition-colors", active ? "text-[#2563FF]" : "text-[#0F172A]")}>{opt.title}</p>
                <p className="text-[13px] text-[#64748B] mt-1 leading-[1.45] font-normal">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        <MultiSelectSearch
          label={translateInterfaceText("Países")}
          placeholder={translateInterfaceText("Buscar e selecionar países")}
          options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
          selected={data.countries}
          onChange={handleCountryChange}
        />

        <div className={cn("space-y-4 transition-all duration-300", data.countries.length === 0 && "opacity-50 pointer-events-none")}>
          {data.countries.length > 1 && (
            <div className="space-y-2">
              <Label className="text-[13px] font-semibold text-[#334155] leading-[1.3]">{translateInterfaceText("País das regiões")}</Label>
              <Select
                value={activeCountryForRegions}
                onValueChange={(v) => setActiveCountryForRegions(v)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={translateInterfaceText("Selecione o país")} />
                </SelectTrigger>
                <SelectContent>
                  {data.countries.map((code) => (
                    <SelectItem key={code} value={code}>
                      {COUNTRIES.find((c) => c.code === code)?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <MultiSelectSearch
            label={activeCountryForRegions ? translateInterfaceText("Regiões ({value0})", { value0: (activeCountryName) }) : translateInterfaceText("Regiões")}
            placeholder={data.countries.length === 0 ? translateInterfaceText("Selecione um país para configurar regiões") : (activeCountryRegions.length > 0 ? translateInterfaceText("Buscar e selecionar regiões") : translateInterfaceText("País sem regiões mapeadas"))}
            options={activeCountryRegions.map((r) => ({ value: r.code, label: r.name }))}
            selected={data.regions.filter(r => activeCountryRegions.some(ar => ar.code === r))}
            onChange={(selectedRegionsInActiveCountry) => {
              const otherRegions = data.regions.filter(r => !activeCountryRegions.some(ar => ar.code === r));
              update({ regions: [...otherRegions, ...selectedRegionsInActiveCountry] });
            }}
          />
        </div>
      </div>
    </>
  );
}

function StepTraffic({ data, update }: StepProps) {
  useLanguage();
  const addRule = () => {
    const rule: UtmRule = {
      id: Math.random().toString(36).slice(2),
      parameter: 'utm_source',
      operator: 'equals',
      value: '',
      action: 'allow',
    };
    update({ utmRules: [...data.utmRules, rule], utmEnabled: true });
  };
  const updateRule = (id: string, patch: Partial<UtmRule>) =>
    update({ utmRules: data.utmRules.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const removeRule = (id: string) => update({ utmRules: data.utmRules.filter((r) => r.id !== id) });

  const urlExample = data.urlKeyEnabled && data.urlKeyName
    ? `?${data.urlKeyName}=${data.urlKeyValue || 'valor'}`
    : '';

  return (
    <>
      <SectionTitle title={translateInterfaceText("Controle por origem de tráfego")} />

      {/* UTM */}
      <div className="rounded-2xl border border-[#EEF2F7] bg-white p-6 mb-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            <p className="text-[14px] font-bold text-[#0F172A]">{translateInterfaceText("Regras de UTM")}</p>
            <p className="text-[13px] text-[#64748B] mt-1 font-normal leading-[1.45]">{translateInterfaceText("Liberar ou bloquear por parâmetros UTM.")}</p>
          </div>
          <Switch 
            checked={data.utmEnabled} 
            onCheckedChange={(v) => update({ utmEnabled: v })} 
            className="data-[state=checked]:bg-[#2563FF]"
          />
        </div>
        
        {data.utmEnabled && (
          <div className="mt-6 space-y-4">
            {data.utmRules.length === 0 ? (
              <div className="p-10 rounded-2xl border border-dashed border-[#E5E7EB] bg-[#F9FAFB] text-center flex flex-col items-center justify-center">
                <p className="text-[14px] text-[#6B7280] mb-6 max-w-[280px]">
                  {translateInterfaceText("Nenhuma regra criada ainda. Adicione condições para liberar ou bloquear acessos.")}</p>
                <Button
                  variant="outline"
                  onClick={addRule}
                  className="h-11 text-[14px] font-bold text-[#2563FF] border-[#D7E4FF] bg-white rounded-xl px-6 transition-all"
                >
                  <Plus className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Adicionar regra UTM")}</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {data.utmRules.map((r, idx) => (
                  <div
                    key={r.id}
                    className="relative p-6 rounded-2xl bg-[#F9FAFB] border border-[#EEF2F7] animate-in fade-in slide-in-from-top-2 duration-300"
                  >
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{translateInterfaceText("REGRA")}{" "}{idx + 1}</span>
                      <button
                        onClick={() => removeRule(r.id)}
                        className="p-1.5 rounded-lg hover:bg-white hover:text-[#EF4444] text-[#9CA3AF] transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-x-5 gap-y-6">
                      <div className="space-y-2">
                        <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Ação")}</Label>
                        <Select
                          value={r.action}
                          onValueChange={(v) => updateRule(r.id, { action: v as UtmAction })}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="allow">{translateInterfaceText("Liberar")}</SelectItem>
                            <SelectItem value="block">{translateInterfaceText("Bloquear")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Parâmetro")}</Label>
                        <Select
                          value={r.parameter}
                          onValueChange={(v) => updateRule(r.id, { parameter: v as UtmParam })}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as UtmParam[]).map(
                              (p) => (
                                <SelectItem key={p} value={p}>
                                  {p}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Condição")}</Label>
                        <Select
                          value={r.operator}
                          onValueChange={(v) => updateRule(r.id, { operator: v as UtmOperator })}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">{translateInterfaceText("é igual a")}</SelectItem>
                            <SelectItem value="contains">{translateInterfaceText("contém")}</SelectItem>
                            <SelectItem value="starts_with">{translateInterfaceText("começa com")}</SelectItem>
                            <SelectItem value="not_contains">{translateInterfaceText("não contém")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[12px] font-semibold text-[#374151]">{translateInterfaceText("Valor")}</Label>
                        <Input
                          value={r.value}
                          onChange={(e) => updateRule(r.id, { value: e.target.value })}
                          placeholder={translateInterfaceText("Ex: facebook")}
                          className="h-11 text-[14px] rounded-xl border-[#E2E8F0] bg-white focus:border-[#2563FF] focus:ring-[#2563FF] transition-all font-medium text-[#334155] placeholder:text-[#94A3B8]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={addRule}
                  className="w-full h-11 text-[14px] font-bold text-[#2563FF] border-[#D7E4FF] bg-white rounded-xl transition-all"
                >
                  <Plus className="w-4 h-4 mr-2" /> {" "}{translateInterfaceText("Adicionar regra UTM")}</Button>
              </div>
            )}
          </div>
        )}
      </div>

    </>
  );
}

function StepDevices({ data, update }: StepProps) {
  useLanguage();
  const toggleDevice = (d: DeviceType) => {
    const has = data.allowedDevices.includes(d);
    const next = has ? data.allowedDevices.filter((x) => x !== d) : [...data.allowedDevices, d];
    update({ allowedDevices: next });
  };

  const devices: { v: DeviceType; label: string; Icon: any }[] = [
    { v: 'desktop', label: translateInterfaceText("Desktop"), Icon: Monitor },
    { v: 'mobile', label: translateInterfaceText("Celular"), Icon: Smartphone },
    { v: 'tablet', label: translateInterfaceText("Tablet"), Icon: Tablet },
  ];

  return (
    <>
      <SectionTitle title={translateInterfaceText("Dispositivos permitidos")} />
      <div className="grid grid-cols-3 gap-3">
        {devices.map(({ v, label, Icon }) => {
          const active = data.allowedDevices.includes(v);
          return (
            <button
              key={v}
              onClick={() => toggleDevice(v)}
              className={cn(
                'flex flex-col items-center justify-center gap-2 h-24 rounded-2xl border transition-all duration-200 shadow-sm',
                active
                    ? 'border-[#2563FF] bg-[#EEF4FF] text-[#2563FF]'
                  : 'border-[#E5E7EB] bg-white text-[#64748B] hover:border-[#D1E0FF] hover:bg-[#F9FAFB]'
              )}
            >
              <Icon className={cn("w-6 h-6 transition-colors", active ? "text-[#2563FF]" : "text-[#94A3B8]")} />
              <span className="text-[13px] font-semibold">{label}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function StepBrowser({ data, update }: StepProps) {
  useLanguage();
  return (
    <>
      <SectionTitle title={translateInterfaceText("Idioma do navegador")} />
      <div className="grid grid-cols-2 gap-3 mb-4">
        {(
          [
            { v: 'allow' as const, label: translateInterfaceText("Permitir selecionados") },
            { v: 'block' as const, label: translateInterfaceText("Bloquear selecionados") },
          ]
        ).map((opt) => {
          const active = data.languageMode === opt.v;
          return (
            <button
              key={opt.v}
              onClick={() => update({ languageMode: opt.v })}
              className={cn(
                'text-center px-4 h-11 rounded-xl border text-[13px] font-semibold transition-all shadow-sm',
                active
                  ? 'border-[#2563FF] bg-[#EEF4FF] text-[#2563FF]'
                  : 'border-[#E5E7EB] bg-white text-[#334155] hover:border-[#D1E0FF]'
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <MultiSelectSearch
        label=""
        placeholder={translateInterfaceText("Buscar e selecionar idiomas")}
        options={LANGUAGES.map((l) => ({ value: l.code, label: l.name }))}
        selected={data.languages}
        onChange={(languages) => update({ languages })}
      />
    </>
  );
}

function StepProxy({ data, update }: StepProps) {
  useLanguage();
  return (
    <>
      <SectionTitle title={translateInterfaceText("Segurança Avançada")} />
      <div className="rounded-2xl border border-[#EEF2F7] bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            <p className="text-[14px] font-bold text-[#0F172A]">{translateInterfaceText("Bloquear Proxy/VPN")}</p>
            <p className="text-[13px] text-[#64748B] mt-1 leading-[1.45] font-normal">
              {translateInterfaceText("Bloqueia acessos suspeitos usando proxy, VPN ou mascaramento de localização.")}</p>
          </div>
          <Switch
            checked={data.blockProxyVpn}
            onCheckedChange={(v) => update({ blockProxyVpn: v })}
            className="data-[state=checked]:bg-[#2563FF]"
          />
        </div>
      </div>
    </>
  );
}


function StepSummary({ data }: { data: VSLProtection }) {
  useLanguage();
  const [sim, setSim] = useState<'allowed' | 'blocked'>('allowed');
  return (
    <>
      <SectionTitle title={translateInterfaceText("Resumo da proteção")} />

      <div className="rounded-2xl border border-[#EEF2F7] bg-[#F9FAFB] p-8 mb-8 shadow-sm">
        <SummaryBlocks data={data} />
      </div>

      <div className="rounded-2xl border border-[#EEF2F7] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <p className="text-[14px] font-bold text-[#0F172A]">{translateInterfaceText("Simular visualização")}</p>
          <div className="inline-flex bg-[#F3F4F6] rounded-xl p-1">
            {(
              [
                { v: 'allowed' as const, label: translateInterfaceText("Permitido"), Icon: Eye },
                { v: 'blocked' as const, label: translateInterfaceText("Bloqueado"), Icon: EyeOff },
              ]
            ).map(({ v, label, Icon }) => {
              const active = sim === v;
              return (
                <button
                  key={v}
                  onClick={() => setSim(v)}
                  className={cn(
                    'flex items-center gap-2 px-5 h-9 rounded-lg text-[12px] font-bold transition-all',
                    active ? 'bg-white text-[#2563FF] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'
                  )}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="aspect-video rounded-2xl bg-[#0F172A] flex items-center justify-center relative overflow-hidden border border-[#EEF2F7] shadow-inner">
          {sim === 'allowed' && data.videoThumb ? (
            <img src={data.videoThumb} alt="" className="w-full h-full object-cover opacity-80" />
          ) : sim === 'blocked' && data.redirectVideoThumb ? (
            <img src={data.redirectVideoThumb} alt="" className="w-full h-full object-cover opacity-80" />
          ) : (
            <p className="text-white/40 text-[13px] font-medium tracking-wide">{translateInterfaceText("PRÉ-VISUALIZAÇÃO")}</p>
          )}
          <div
            className={cn(
              'absolute top-4 left-4 px-4 h-8 inline-flex items-center text-[12px] font-bold rounded-full shadow-lg backdrop-blur-md',
              sim === 'allowed' ? 'bg-[#DCE7FF]/90 text-[#2563FF]' : 'bg-[#FEE2E2]/90 text-[#DC2626]'
            )}
          >
            {sim === 'allowed' ? translateInterfaceText("Acesso permitido") : translateInterfaceText("Acesso bloqueado")}
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- SUMMARY SIDEBAR ---------- */

function SummarySidebar({ data }: { data: VSLProtection }) {
  useLanguage();
  return (
    <div className="px-6 py-6 space-y-6">
      <SummaryBlocks data={data} compact />
    </div>
  );
}

function SummaryBlocks({ data, compact }: { data: VSLProtection; compact?: boolean }) {
  useLanguage();
  const countryNames = data.countries.map((c) => COUNTRIES.find((x) => x.code === c)?.name ?? c);
  
  // Resolve region names based on the countries selected
  const regionNames = data.regions.map((regionCode) => {
    for (const countryCode of data.countries) {
      const regions = REGIONS_BY_COUNTRY[countryCode] || [];
      const found = regions.find(r => r.code === regionCode);
      if (found) return found.name;
    }
    return regionCode;
  });

  const langNames = data.languages.map((c) => LANGUAGES.find((x) => x.code === c)?.name ?? c);

  return (
    <div className={cn('space-y-8', compact && 'text-[13px]')}>
      <Block title={translateInterfaceText("VSL PROTEGIDA")}>
        {data.videoName ? (
          <div className="flex items-center gap-2">
            <Film className="w-3.5 h-3.5 text-[#2563FF]" />
            <p className="text-[#334155] font-semibold truncate">{data.videoName}</p>
          </div>
        ) : (
          <p className="text-[#9CA3AF] font-medium">{translateInterfaceText("Nenhuma selecionada")}</p>
        )}
      </Block>

      <Block title={translateInterfaceText("LOCALIZAÇÃO")}>
        <div className="space-y-2">
          <p className="text-[#334155] font-semibold leading-[1.45]">
            {data.locationMode === 'allow' ? translateInterfaceText("Permitir apenas") : translateInterfaceText("Bloquear")}:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {countryNames.length ? (
              countryNames.map((c) => (
                <span key={c} className="bg-[#EEF4FF] text-[#2563FF] px-2.5 py-1 rounded-lg text-[11px] font-bold border border-[#D7E4FF]">
                  {c}
                </span>
              ))
            ) : (
              <span className="text-[#9CA3AF] font-medium">{translateInterfaceText("Nenhum selecionado")}</span>
            )}
          </div>
          
          {data.countries.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#EEF2F7] space-y-3">
              <p className="text-[11px] font-extrabold text-[#94A3B8] uppercase tracking-[0.055em] mb-2">{translateInterfaceText("REGIÕES")}</p>
              
              {data.regions.length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF] font-medium">
                  {data.locationMode === 'allow' ? translateInterfaceText("Todas as regiões permitidas") : translateInterfaceText("Todas as regiões bloqueadas")}
                </p>
              ) : (
                <div className="space-y-3">
                  {data.countries.map(countryCode => {
                    const countryName = COUNTRIES.find(c => c.code === countryCode)?.name || countryCode;
                    const regionsInCountry = REGIONS_BY_COUNTRY[countryCode] || [];
                    const selectedInCountry = data.regions.filter(rCode => regionsInCountry.some(ar => ar.code === rCode));
                    
                    if (selectedInCountry.length === 0) return null;

                    return (
                      <div key={countryCode} className="space-y-1.5">
                        <p className="text-[11px] font-bold text-[#374151]">{countryName}:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedInCountry.map(rCode => {
                            const rName = regionsInCountry.find(r => r.code === rCode)?.name || rCode;
                            return (
                              <span key={rCode} className="bg-[#F9FAFB] text-[#6B7280] px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-[#EEF2F7]">
                                {rName}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </Block>

      <Block title={translateInterfaceText("TRÁFEGO")}>
        <div className="space-y-3">
          {data.utmEnabled && data.utmRules.length > 0 ? (
            <div className="space-y-2">
              <p className="text-[#334155] font-semibold leading-[1.45]">{data.utmRules.length} {" "}{translateInterfaceText("regra")}{data.utmRules.length !== 1 ? 's' : ''} {" "}{translateInterfaceText("UTM criada")}{data.utmRules.length !== 1 ? 's' : ''}</p>
              <ul className="space-y-1.5">
                {data.utmRules.slice(0, 3).map((r) => (
                  <li key={r.id} className="text-[#6B7280] flex items-center gap-2 leading-tight">
                    <div className="w-1 h-1 rounded-full bg-[#2563EB] shrink-0" />
                    <span className="truncate text-[12px]">
                      {r.action === 'allow' ? translateInterfaceText("Lib.") : translateInterfaceText("Bloq.")} {r.parameter} {opLabelShort(r.operator)} <span className="font-semibold text-[#374151]">{r.value || translateInterfaceText("vazio")}</span>
                    </span>
                  </li>
                ))}
                {data.utmRules.length > 3 && (
                  <li className="text-[11px] text-[#2563FF] font-extrabold tracking-[0.055em]">+{data.utmRules.length - 3} {" "}{translateInterfaceText("outras regras...")}</li>
                )}
              </ul>
            </div>
          ) : (
            <p className="text-[#9CA3AF] font-medium">{translateInterfaceText("Sem regras UTM")}</p>
          )}
          
        </div>
      </Block>

      <Block title={translateInterfaceText("SEGURANÇA")}>
        <div className="space-y-2.5">
          <p className="text-[#334155] font-semibold leading-[1.45]">
            {data.allowedDevices.length === 3
              ? translateInterfaceText("Todos dispositivos")
              : data.allowedDevices.length === 0 
                ? translateInterfaceText("Nenhum dispositivo")
                : translateInterfaceText("Permitido: {value0}", { value0: (data.allowedDevices.join(', ')) })}
          </p>
          <div className="flex items-center gap-2">
            <ShieldCheck className={cn("w-4 h-4", data.blockProxyVpn ? "text-[#10B981]" : "text-[#9CA3AF]")} />
            <p className="text-[#64748B] font-medium text-[13px] leading-[1.45]">
              {data.blockProxyVpn ? translateInterfaceText("Proxy/VPN bloqueado") : translateInterfaceText("Proxy/VPN liberado")}
            </p>
          </div>
          {langNames.length > 0 && (
            <div className="pt-2 border-t border-[#EEF2F7]">
              <p className="text-[11px] font-extrabold text-[#94A3B8] uppercase tracking-[0.055em] mb-1.5">{translateInterfaceText("IDIOMAS")}</p>
              <p className="text-[#374151] font-semibold text-[12px]">{langNames.join(', ')}</p>
            </div>
          )}
        </div>
      </Block>

      <Block title={translateInterfaceText("VÍDEO ALTERNATIVO")}>
        {data.redirectVideoId ? (
          <div className="flex items-center gap-2">
            <Film className="w-3.5 h-3.5 text-[#2563FF]" />
            <p className="text-[#334155] font-semibold truncate leading-[1.45]">{data.redirectVideoName || translateInterfaceText("Selecionado")}</p>
          </div>
        ) : (
          <p className="text-[#9CA3AF] font-medium">{translateInterfaceText("Nenhum vídeo alternativo selecionado")}</p>
        )}
      </Block>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  useLanguage();
  return (
    <div className="animate-in fade-in slide-in-from-right-2 duration-400">
      <p className="text-[11px] uppercase tracking-[0.055em] text-[#94A3B8] font-extrabold mb-3">
        {title}
      </p>
      <div className="text-[#374151] leading-relaxed">{children}</div>
    </div>
  );
}

function opLabelShort(op: UtmOperator) {
  switch (op) {
    case 'equals': return '=';
    case 'contains': return '~';
    case 'starts_with': return '^';
    case 'not_contains': return '!~';
  }
}

function opLabel(op: UtmOperator) {
  switch (op) {
    case 'equals': return translateInterfaceText("é igual a");
    case 'contains': return translateInterfaceText("contém");
    case 'starts_with': return translateInterfaceText("começa com");
    case 'not_contains': return translateInterfaceText("não contém");
  }
}

